import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { X, Loader2, FileUp } from 'lucide-react';
import { toast } from '../lib/toast';

interface AIAgentCreatorProps {
  onSuccess: () => void;
  onCancel: () => void;
  initialData?: {
    id: string;
    name: string;
    goals: string;
    system_prompt: string;
    instructions?: string;
    attachment_url?: string;
    attachment_name?: string;
    agent_type?: 'text' | 'image';
  };
}

export default function AIAgentCreator({ onSuccess, onCancel, initialData }: AIAgentCreatorProps) {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(initialData?.name || '');
  const [goals, setGoals] = useState(initialData?.goals || '');
  const [agentInstructions, setAgentInstructions] = useState(initialData?.instructions || '');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !goals.trim() || !agentInstructions.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let attachment_url = initialData?.attachment_url || null;
      let attachment_name = initialData?.attachment_name || null;

      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('File size must be less than 5MB');
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;

        const { error: uploadError, data } = await supabase.storage
          .from('ai-agent-attachments')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        if (data) {
          const { data: { publicUrl } } = supabase.storage
            .from('ai-agent-attachments')
            .getPublicUrl(fileName);
          
          attachment_url = publicUrl;
          attachment_name = file.name;
        }
      }

      const agentData = {
        name,
        goals,
        system_prompt: agentInstructions,
        instructions: agentInstructions,
        attachment_url,
        attachment_name,
        user_id: user.id,
        is_custom: true,
        agent_type: 'text',
        updated_at: new Date().toISOString()
      };

      if (initialData?.id) {
        const { error } = await supabase
          .from('ai_agents')
          .update(agentData)
          .eq('id', initialData.id);

        if (error) throw error;
        toast.success('AI agent updated successfully');
      } else {
        const { error } = await supabase
          .from('ai_agents')
          .insert([agentData]);

        if (error) throw error;
        toast.success('AI agent created successfully');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving AI agent:', error);
      setError(error instanceof Error ? error.message : 'Failed to save AI agent');
      toast.error('Failed to save AI agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        {initialData ? 'Edit AI Agent' : 'Create Custom AI Agent'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Agent Name *
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter agent name"
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="goals" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Agent Goals *
          </label>
          <textarea
            id="goals"
            rows={3}
            value={goals}
            onChange={(e) => setGoals(e.target.value)}
            placeholder="Define the agent's goals and objectives"
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="instructions" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Instructions
          </label>
          <textarea
            id="instructions"
            rows={3}
            value={agentInstructions}
            onChange={(e) => setAgentInstructions(e.target.value)}
            placeholder="Enter system prompt that defines the agent's behavior"
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required
            aria-required="true"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Attachment (Max 5MB)
          </label>
          <div className="flex items-center space-x-4">
            <label
              htmlFor="file-upload"
              className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
            >
              <FileUp className="h-5 w-5 inline-block mr-2" />
              Choose file
            </label>
            <input
              id="file-upload"
              type="file"
              className="sr-only"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {(file || initialData?.attachment_name) && (
              <span className="text-sm text-gray-500">
                <span className="font-medium">
                  {file?.name || initialData?.attachment_name}
                </span>
                <button
                  type="button"
                  className="ml-2 text-indigo-600 hover:text-indigo-500"
                  onClick={() => {
                    setFile(null);
                    // If we're editing, we need to clear the attachment fields
                    if (initialData) {
                      initialData.attachment_url = undefined;
                      initialData.attachment_name = undefined;
                    }
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 p-4 border border-red-200">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                {initialData ? 'Updating Agent...' : 'Creating Agent...'}
              </>
            ) : (
              initialData ? 'Update Agent' : 'Create Agent'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}